import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntake, useSaveIntake, type IntakeData } from '@pelvia/shared/hooks/patient/useIntake'
import { useConnectWearable, useWearableStatus } from '@pelvia/shared/hooks/patient/useWearables'
import {
  PHQ9_PROMPT,
  PHQ9_QUESTIONS,
  GAD7_PROMPT,
  GAD7_QUESTIONS,
  SCREENER_FREQUENCY_OPTIONS,
  screenerScore,
} from '@pelvia/shared/lib/patient/screeners'
import { generateHandle } from '@/lib/generateHandle'
import { captureEvent } from '@/lib/analytics'
import { conditionToLearnSlug, getLearnPage } from '@/pages/learn/learnPages'
import { painColor, painTextColor } from '@pelvia/shared/lib/patient/painColors'
import { api } from '@/lib/api'
import './patient.css'

const TOTAL_STEPS = 7

const PRONOUN_OPTIONS = [
  { value: 'she_her', label: 'She/her' },
  { value: 'they_them', label: 'They/them' },
  { value: 'he_him', label: 'He/him' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
]

const CONDITION_OPTIONS = [
  { value: 'no_diagnosis', label: "I don't have a diagnosis yet" },
  { value: 'pelvic_tracking', label: 'Just tracking my pelvic health' },
  { value: 'general_wellness', label: 'General wellness & cycle tracking' },
  { value: 'endometriosis', label: 'Endometriosis' },
  { value: 'fibroids', label: 'Fibroids' },
  { value: 'adenomyosis', label: 'Adenomyosis' },
  { value: 'pelvic_floor_dysfunction', label: 'Pelvic floor dysfunction' },
  { value: 'interstitial_cystitis', label: 'Interstitial cystitis' },
  { value: 'chronic_pelvic_pain', label: 'Chronic pelvic pain' },
  { value: 'mood_swings', label: 'Mood swings' },
  { value: 'painful_periods', label: 'Painful periods' },
  { value: 'cramps', label: 'Cramps' },
  { value: 'painful_intercourse', label: 'Painful intercourse' },
  { value: 'painful_bowel_movements', label: 'Painful bowel movements' },
  { value: 'something_else', label: 'Other' },
]

// Conditions that imply a pain experience — we show the duration picker only
// for these. "Just tracking" and "wellness" users don't need it.
const PAIN_CONDITIONS = new Set([
  'endometriosis',
  'fibroids',
  'adenomyosis',
  'pelvic_floor_dysfunction',
  'interstitial_cystitis',
  'chronic_pelvic_pain',
  'painful_periods',
  'cramps',
  'painful_intercourse',
  'painful_bowel_movements',
  'something_else',
])

const DURATION_OPTIONS = [
  { value: 'less_than_1', label: 'Less than 1 year' },
  { value: '1_3', label: '1–3 years' },
  { value: '3_5', label: '3–5 years' },
  { value: '5_10', label: '5–10 years' },
  { value: '10_plus', label: '10+ years' },
]

const PREGNANT_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'trying', label: 'Trying' },
  { value: 'not_sure', label: 'Not sure' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
]

const CYCLE_LENGTH_OPTIONS = [
  { value: 'less_than_21', label: '< 21 days' },
  { value: '21_25', label: '21–25' },
  { value: '26_30', label: '26–30' },
  { value: '31_35', label: '31–35' },
  { value: '35_plus', label: '35+' },
  { value: 'irregular', label: 'Irregular' },
  { value: 'not_sure', label: 'Not sure' },
  { value: 'not_applicable', label: 'N/A' },
]

const PERIOD_DURATION_OPTIONS = [
  { value: '1_2', label: '1–2 days' },
  { value: '3_4', label: '3–4 days' },
  { value: '5_6', label: '5–6 days' },
  { value: '7_plus', label: '7+ days' },
  { value: 'irregular', label: 'Irregular' },
  { value: 'not_applicable', label: 'N/A' },
]

// Heavy-bleeding treatment history — anemia management context for the consult
// note. Asked on the cycle step.
const YES_NO_UNSURE_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'not_sure', label: 'Not sure' },
]

const CONDITION_TO_DIAGNOSIS: Record<string, string> = {
  endometriosis: 'endometriosis',
  adenomyosis: 'adenomyosis',
  chronic_pelvic_pain: 'cpp',
  pelvic_floor_dysfunction: 'cpp',
  interstitial_cystitis: 'interstitial_cystitis',
  fibroids: 'other',
  something_else: 'other',
}

const CYCLE_LENGTH_DAYS: Record<string, number> = {
  less_than_21: 20,
  '21_25': 23,
  '26_30': 28,
  '31_35': 33,
  '35_plus': 36,
}

type DraftState = IntakeData & { step: number }

const DEFAULT_DRAFT: DraftState = {
  step: 0,
  display_name: '',
  age: null,
  pronouns: null,
  pronoun_other: '',
  conditions: [],
  pain_duration: null,
  something_else_detail: '',
  is_pregnant: null,
  menarche_age: '',
  cycle_length: null,
  period_duration: null,
  iv_iron: null,
  blood_transfusions: null,
  lmp_ranges: [],
  phq9_responses: Array(PHQ9_QUESTIONS.length).fill(null),
  gad7_responses: Array(GAD7_QUESTIONS.length).fill(null),
}

const WEARABLE_SKIP_KEY = 'pelvia_wearable_skipped'

interface RecallPrefill {
  days: string[]
  pain: Record<string, { score: number; estimated: boolean }>
  medications: { id: number; name: string }[]
}

// The 7 days before today (ascending, en-CA = local YYYY-MM-DD), matching the
// server's recall window. Fallback if the prefill request fails.
function lastSevenDays(): string[] {
  const arr: string[] = []
  for (let i = 7; i >= 1; i--) {
    const d = new Date()
    d.setDate(d.getDate() - i)
    arr.push(d.toLocaleDateString('en-CA'))
  }
  return arr
}

function recallDayLabel(iso: string): string {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

export default function IntakePage() {
  const navigate = useNavigate()
  const { data: existing, isLoading } = useIntake()
  const saveMutation = useSaveIntake()

  const [draft, setDraft] = useState<DraftState>(DEFAULT_DRAFT)
  const [ageError, setAgeError] = useState<string | null>(null)
  const [showCelebration, setShowCelebration] = useState(false)
  const [celebrationLearnSlug, setCelebrationLearnSlug] = useState<string | null>(null)

  // Step 7 "recall your last 7 days" state. Pain is a per-day score; adherence
  // is per-day, per-med; meds created here are persisted immediately (we need
  // their ids for the adherence grid). Estimated days come pre-filled from the
  // wearable and are saved as confirmed user logs on completion.
  const [recallDays, setRecallDays] = useState<string[]>([])
  const [recallPain, setRecallPain] = useState<Record<string, number>>({})
  const [recallAdh, setRecallAdh] = useState<Record<string, Record<number, boolean>>>({})
  const [recallMeds, setRecallMeds] = useState<{ id: number; name: string }[]>([])
  const [recallEstimated, setRecallEstimated] = useState<Set<string>>(new Set())
  const [recallLoaded, setRecallLoaded] = useState(false)

  const wearables = useWearableStatus()
  const connectWearable = useConnectWearable()
  const isWearableConnected = useCallback(
    (provider: string) =>
      (wearables.data?.integrations || []).some(
        (i) => i.provider === provider && (i.status === 'active' || i.status === 'syncing'),
      ),
    [wearables.data],
  )
  const ouraConnected = isWearableConnected('oura')
  const whoopConnected = isWearableConnected('whoop')
  const anyWearableConnected = ouraConnected || whoopConnected

  // Hydrate draft from server if resuming mid-intake.
  useEffect(() => {
    if (!existing?.intake) return
    if (showCelebration) return
    const server = existing.intake
    if (server.intake_completed) {
      navigate('/patient', { replace: true })
      return
    }
    const completedStep = server.completed_step || 0
    // Fresh users start at Step 0 (wearables). Resuming users skip it unless
    // they've never hit Step 1 and haven't explicitly skipped.
    const alreadySkippedWearable =
      typeof window !== 'undefined' && localStorage.getItem(WEARABLE_SKIP_KEY) === '1'
    let resumeStep: number
    if (completedStep >= 1) {
      resumeStep = Math.min(TOTAL_STEPS, completedStep + 1)
    } else if (alreadySkippedWearable || anyWearableConnected) {
      resumeStep = 1
    } else {
      resumeStep = 0
    }
    setDraft((prev) => ({
      ...prev,
      ...server,
      step: prev.step > 0 ? prev.step : resumeStep,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing, anyWearableConnected])

  const patch = useCallback((updates: Partial<DraftState>) => {
    setDraft((prev) => ({ ...prev, ...updates }))
  }, [])

  const toggleArray = useCallback(
    (key: keyof DraftState, value: string) => {
      setDraft((prev) => {
        const cur = (prev[key] as string[]) || []
        const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]
        return { ...prev, [key]: next }
      })
    },
    [],
  )

  const hasPainCondition = useMemo(
    () => (draft.conditions || []).some((c) => PAIN_CONDITIONS.has(c)),
    [draft.conditions],
  )
  const somethingElseSelected = (draft.conditions || []).includes('something_else')

  const saveStep = useCallback(
    async (stepJustFinished: number, extras: Partial<IntakeData> = {}) => {
      const payload: IntakeData = {
        ...draftPayload(draft),
        ...extras,
        completed_step: stepJustFinished,
      }
      await saveMutation.mutateAsync(payload)
    },
    [draft, saveMutation],
  )

  const goNext = useCallback(async () => {
    // Validations
    if (draft.step === 2 && draft.age != null) {
      const age = Number(draft.age)
      if (Number.isNaN(age) || age < 13) {
        setAgeError('You must be at least 13 years old to use Pelvia.')
        return
      }
      if (age > 120) {
        setAgeError('Please enter a valid age.')
        return
      }
    }
    setAgeError(null)

    if (draft.step === TOTAL_STEPS) {
      // Persist the 7-day recall (pain per day + adherence per med/day) before
      // marking intake complete. Best-effort — one failed row shouldn't block a
      // user from finishing onboarding.
      const recallPosts: Promise<unknown>[] = []
      for (const [d, score] of Object.entries(recallPain)) {
        recallPosts.push(api.post('/api/patient/pain-log', { pain_score: score, log_date: d }))
      }
      for (const [d, medMap] of Object.entries(recallAdh)) {
        for (const [mid, taken] of Object.entries(medMap)) {
          if (taken) {
            recallPosts.push(
              api.post('/api/patient/medications/adherence', {
                medication_id: Number(mid),
                taken: true,
                log_date: d,
              }),
            )
          }
        }
      }
      if (recallPosts.length) await Promise.allSettled(recallPosts)

      // If we don't already have a display_name (old flows asked for one; new
      // intake skips it), mint a playful handle so the dashboard greeting has
      // something personal to render. User can always edit in Settings.
      const extras: Partial<IntakeData> = { intake_completed: true }
      if (!(draft.display_name || '').trim()) {
        extras.display_name = generateHandle()
      }
      await saveStep(TOTAL_STEPS, extras)
      // coarse categorical only — no free text, no raw scores/age
      captureEvent('intake_completed', intakeAnalyticsProps(draft, anyWearableConnected))

      // Pick the highest-priority Learn slug for this user's conditions.
      const conds = draft.conditions ?? []
      const slug = conds.includes('endometriosis') ? 'endometriosis'
        : conds.includes('adenomyosis') ? 'adenomyosis'
        : conds.some((c) => conditionToLearnSlug(c) === 'treatment') ? 'treatment'
        : null
      setCelebrationLearnSlug(slug)
      setShowCelebration(true)

      // If there's a Learn card to show, let the user dismiss manually.
      // Otherwise auto-navigate after the standard celebration delay.
      if (!slug) {
        setTimeout(() => navigate('/patient', { replace: true }), 2200)
      }
      return
    }

    if (draft.step > 1) await saveStep(draft.step)
    setDraft((prev) => ({ ...prev, step: Math.min(TOTAL_STEPS, prev.step + 1) }))
  }, [draft, saveStep, navigate, recallPain, recallAdh])

  const goBack = useCallback(() => {
    if (draft.step > 0) {
      setDraft((prev) => ({ ...prev, step: prev.step - 1 }))
    }
  }, [draft.step])

  const skipWearable = useCallback(() => {
    try { localStorage.setItem(WEARABLE_SKIP_KEY, '1') } catch { /* quota */ }
    setDraft((prev) => ({ ...prev, step: 1 }))
  }, [])

  const continueFromWearable = useCallback(() => {
    try { localStorage.removeItem(WEARABLE_SKIP_KEY) } catch { /* quota */ }
    setDraft((prev) => ({ ...prev, step: 1 }))
  }, [])

  // Load the recall prefill (last-7-day window + any wearable estimates + meds)
  // the first time we land on the recall step.
  useEffect(() => {
    if (draft.step !== TOTAL_STEPS || recallLoaded) return
    let cancelled = false
    api
      .get<RecallPrefill>('/api/patient/onboarding/recall')
      .then((res) => {
        if (cancelled) return
        setRecallDays(res.days?.length ? res.days : lastSevenDays())
        setRecallMeds(res.medications || [])
        const seeded: Record<string, number> = {}
        const est = new Set<string>()
        for (const [d, p] of Object.entries(res.pain || {})) {
          if (p && typeof p.score === 'number') {
            seeded[d] = Math.max(0, Math.min(10, Math.round(p.score)))
            if (p.estimated) est.add(d)
          }
        }
        setRecallPain(seeded)
        setRecallEstimated(est)
        setRecallLoaded(true)
      })
      .catch(() => {
        if (cancelled) return
        setRecallDays(lastSevenDays())
        setRecallLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [draft.step, recallLoaded])

  // Set/clear a day's recalled pain. Editing a pre-filled day drops its
  // "estimated" tag — it's now the user's own recollection.
  const setRecallPainFor = useCallback((day: string, score: number | null) => {
    setRecallPain((prev) => {
      const next = { ...prev }
      if (score == null) delete next[day]
      else next[day] = score
      return next
    })
    setRecallEstimated((prev) => {
      if (!prev.has(day)) return prev
      const next = new Set(prev)
      next.delete(day)
      return next
    })
  }, [])

  const toggleRecallAdh = useCallback((day: string, medId: number) => {
    setRecallAdh((prev) => {
      const dayMap = { ...(prev[day] || {}) }
      dayMap[medId] = !dayMap[medId]
      return { ...prev, [day]: dayMap }
    })
  }, [])

  const addRecallMed = useCallback(async (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      const res = await api.post<{ id: number }>('/api/patient/medications', { name: trimmed })
      setRecallMeds((prev) => [...prev, { id: res.id, name: trimmed }])
    } catch {
      /* ignore add failure — the user can retry */
    }
  }, [])

  if (isLoading) {
    return <div className="patient-loading">Loading…</div>
  }

  const ctaLabel =
    draft.step === 0
      ? 'Continue'
      : draft.step === 1
      ? "Let's go"
      : draft.step === TOTAL_STEPS
      ? 'Complete'
      : 'Next'
  const saving = saveMutation.isPending

  return (
    <div className="patient-shell intake-shell">
      <div className="intake-overlay active">
        <div className="intake-modal">
        <div className="intake-progress">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={`intake-dot${i === draft.step - 1 ? ' current' : ''}${i < draft.step - 1 ? ' completed' : ''}`}
            />
          ))}
        </div>

        {draft.step === 0 && (
          <Step0Wearables
            connected={{ oura: ouraConnected, whoop: whoopConnected }}
            pendingProvider={connectWearable.isPending ? connectWearable.variables ?? null : null}
            onConnect={(provider) => connectWearable.mutate(provider)}
          />
        )}

        {draft.step === 1 && <Step1Welcome />}

        {draft.step === 2 && (
          <Step2About
            draft={draft}
            patch={patch}
            ageError={ageError}
          />
        )}

        {draft.step === 3 && (
          <Step3Story
            draft={draft}
            patch={patch}
            toggleArray={toggleArray}
            hasPainCondition={hasPainCondition}
            somethingElseSelected={somethingElseSelected}
          />
        )}

        {draft.step === 4 && <Step4Cycle draft={draft} patch={patch} />}

        {draft.step === 5 && <Step5Periods draft={draft} patch={patch} />}

        {draft.step === 6 && <Step6Wellbeing draft={draft} patch={patch} />}

        {draft.step === 7 && (
          <Step7Recall
            days={recallDays}
            meds={recallMeds}
            pain={recallPain}
            onSetPain={setRecallPainFor}
            adh={recallAdh}
            onToggleAdh={toggleRecallAdh}
            estimated={recallEstimated}
            loaded={recallLoaded}
            hasWearable={anyWearableConnected}
            onAddMed={addRecallMed}
          />
        )}

        <div className="intake-nav">
          <button
            type="button"
            className={`btn btn-secondary${draft.step <= 1 ? ' intake-skip-btn' : ''}`}
            onClick={
              draft.step === 0
                ? skipWearable
                : draft.step === 1
                ? () => navigate('/patient', { replace: true })
                : goBack
            }
            disabled={saving || connectWearable.isPending}
          >
            {draft.step === 0 ? 'Skip for now' : draft.step === 1 ? 'Skip for now' : 'Back'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={draft.step === 0 ? continueFromWearable : goNext}
            disabled={saving || connectWearable.isPending}
          >
            {connectWearable.isPending ? 'Redirecting…' : saving ? 'Saving…' : ctaLabel}
          </button>
        </div>
        </div>
      </div>

      {showCelebration && (() => {
        const learnPage = celebrationLearnSlug ? getLearnPage(celebrationLearnSlug) : null
        return (
          <div className="celebration-overlay active">
            <div className="celebration-content">
              <div className="celebration-check">
                <svg viewBox="0 0 52 52" className="celebration-svg">
                  <circle cx="26" cy="26" r="24" fill="none" stroke="#4CAF50" strokeWidth="2" />
                  <path
                    d="M14 27l7 7 16-16"
                    fill="none"
                    stroke="#4CAF50"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="celebration-title">You're all set!</h2>
              <p className="celebration-subtitle">Your profile is ready. Let's start tracking your journey.</p>

              {learnPage && (
                <div className="celebration-learn-card">
                  <p className="celebration-learn-label">Before you go — a quick read:</p>
                  <a
                    href={learnPage.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="celebration-learn-link"
                    onClick={() => captureEvent('learn_page_viewed', { slug: learnPage.slug, source: 'post_intake' })}
                  >
                    <span className="celebration-learn-title">{learnPage.title}</span>
                    <span className="celebration-learn-one-liner">{learnPage.oneLiner}</span>
                    <span className="celebration-learn-cta">Read →</span>
                  </a>
                </div>
              )}

              {learnPage && (
                <button
                  type="button"
                  className="btn btn-primary celebration-continue-btn"
                  onClick={() => navigate('/patient', { replace: true })}
                >
                  Continue to dashboard
                </button>
              )}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// Shape the draft for the API (strip `step`, derive primary_diagnosis + cycle_length_days)
function draftPayload(draft: DraftState): IntakeData {
  const conds = draft.conditions || []
  const mapped = conds.find((c) => CONDITION_TO_DIAGNOSIS[c])
  const cycleDays = draft.cycle_length ? CYCLE_LENGTH_DAYS[draft.cycle_length] : undefined

  const ranges = draft.lmp_ranges || []
  const lastStart =
    ranges.length > 0
      ? [...ranges].sort((a, b) => b.start.localeCompare(a.start))[0].start
      : draft.last_period_start

  const lmpDates: string[] = []
  for (const r of ranges) {
    const start = new Date(r.start)
    const end = new Date(r.end)
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      lmpDates.push(d.toISOString().slice(0, 10))
    }
  }

  return {
    display_name: draft.display_name,
    age: draft.age,
    pronouns: draft.pronouns,
    pronoun_other: draft.pronoun_other,
    conditions: draft.conditions,
    pain_duration: draft.pain_duration,
    something_else_detail: draft.something_else_detail,
    primary_diagnosis: mapped ? CONDITION_TO_DIAGNOSIS[mapped] : undefined,
    is_pregnant: draft.is_pregnant,
    menarche_age: draft.menarche_age || null,
    cycle_length: draft.cycle_length,
    cycle_length_days: cycleDays,
    period_duration: draft.period_duration,
    iv_iron: draft.iv_iron,
    blood_transfusions: draft.blood_transfusions,
    lmp_ranges: ranges,
    lmp_dates: lmpDates,
    last_period_start: lastStart,
    phq9_responses: draft.phq9_responses,
    phq9_score: screenerScore(draft.phq9_responses),
    gad7_responses: draft.gad7_responses,
    gad7_score: screenerScore(draft.gad7_responses),
  }
}

// Coarse categorical props for the intake_completed analytics event.
// No free text, no raw scores/age — all fields are enum buckets only.
function intakeAnalyticsProps(draft: DraftState, wearableConnected: boolean): Record<string, unknown> {
  const conds = draft.conditions ?? []
  const mapped = conds.find((c) => CONDITION_TO_DIAGNOSIS[c])
  const primaryDiag = mapped ? CONDITION_TO_DIAGNOSIS[mapped] : 'none'

  const phq9 = screenerScore(draft.phq9_responses ?? [])
  const gad7 = screenerScore(draft.gad7_responses ?? [])
  const rawSeverity = Math.max(phq9 ?? -1, gad7 ?? -1)
  const severityBucket =
    rawSeverity < 0 ? 'unknown' : rawSeverity < 10 ? 'low' : rawSeverity < 15 ? 'moderate' : 'high'

  const age = Number(draft.age)
  const ageGroup = isNaN(age)
    ? 'unknown'
    : age < 18 ? '13_17'
    : age < 25 ? '18_24'
    : age < 35 ? '25_34'
    : age < 45 ? '35_44'
    : age < 55 ? '45_54'
    : '55_plus'

  return {
    primary_diagnosis: primaryDiag,
    condition_count: conds.length,
    conditions: conds,
    pain_duration: draft.pain_duration ?? 'none',
    severity_bucket: severityBucket,
    age_group: ageGroup,
    has_wearable: wearableConnected,
  }
}

// ---------- Step 1: Welcome ----------
function Step1Welcome() {
  return (
    <div className="intake-step active">
      <div className="intake-welcome">
        <div className="intake-welcome-icon">
          <svg viewBox="0 0 48 48" fill="none" stroke="#7B5E80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="48" height="48">
            <circle cx="24" cy="24" r="20" />
            <path d="M16 28c2 3 5 5 8 5s6-2 8-5" />
            <circle cx="18" cy="20" r="1.5" fill="#7B5E80" stroke="none" />
            <circle cx="30" cy="20" r="1.5" fill="#7B5E80" stroke="none" />
          </svg>
        </div>
        <h2 className="intake-title" style={{ textAlign: 'center' }}>Welcome to Pelvia</h2>
        <p className="intake-desc" style={{ textAlign: 'center', maxWidth: '360px', margin: '0 auto 1.5rem' }}>
          We have a few quick questions to personalize your experience. Answer what you can — skip what you can't.
          Everything can be updated later in Settings.
        </p>
        <div className="intake-welcome-features">
          <div className="intake-welcome-feature">
            <span className="intake-welcome-num">5</span>
            <span>short steps</span>
          </div>
          <div className="intake-welcome-feature">
            <span className="intake-welcome-num">2</span>
            <span>minutes</span>
          </div>
          <div className="intake-welcome-feature">
            <span className="intake-welcome-num">0</span>
            <span>required</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------- Step 2: About you ----------
function Step2About({
  draft,
  patch,
  ageError,
}: {
  draft: DraftState
  patch: (updates: Partial<DraftState>) => void
  ageError: string | null
}) {
  return (
    <div className="intake-step active">
      <h2 className="intake-title">About you</h2>
      <p className="intake-desc">
        Just a couple things to make Pelvia yours. We'll generate a handle for
        you after — change it any time in Settings.
      </p>

      <div className="form-field">
        <label className="form-label">How old are you?</label>
        <input
          className="form-input"
          type="text"
          inputMode="numeric"
          placeholder="e.g. 28"
          style={{ maxWidth: 120 }}
          value={draft.age == null ? '' : String(draft.age)}
          onChange={(e) => patch({ age: e.target.value ? parseInt(e.target.value, 10) : null })}
        />
        {ageError && <div className="checkin-error" role="alert">{ageError}</div>}
      </div>

      <div className="form-field">
        <label className="form-label">Pronouns</label>
        <ChipGroup
          options={PRONOUN_OPTIONS}
          value={draft.pronouns || null}
          onChange={(v) => patch({ pronouns: v })}
        />
      </div>

      {draft.pronouns === 'other' && (
        <div className="form-field">
          <label className="form-label">Please specify</label>
          <input
            className="form-input"
            placeholder="Your preferred pronouns"
            value={draft.pronoun_other || ''}
            onChange={(e) => patch({ pronoun_other: e.target.value })}
          />
        </div>
      )}
    </div>
  )
}

// ---------- Step 3: Your story ----------
function Step3Story({
  draft,
  patch,
  toggleArray,
  hasPainCondition,
  somethingElseSelected,
}: {
  draft: DraftState
  patch: (updates: Partial<DraftState>) => void
  toggleArray: (key: keyof DraftState, value: string) => void
  hasPainCondition: boolean
  somethingElseSelected: boolean
}) {
  return (
    <div className="intake-step active">
      <h2 className="intake-title">Your story</h2>
      <p className="intake-desc">You don't need a diagnosis to use Pelvia. Just tell us what you experience.</p>

      <div className="form-field">
        <label className="form-label">
          What brings you here? <span className="form-hint">(select all that apply)</span>
        </label>
        <MultiChipGroup
          options={CONDITION_OPTIONS}
          values={draft.conditions || []}
          onToggle={(v) => toggleArray('conditions', v)}
        />
      </div>

      {somethingElseSelected && (
        <div className="form-field">
          <label className="form-label">Please describe</label>
          <input
            className="form-input"
            placeholder="What are you experiencing?"
            value={draft.something_else_detail || ''}
            onChange={(e) => patch({ something_else_detail: e.target.value })}
          />
        </div>
      )}

      {hasPainCondition && (
        <div className="form-field">
          <label className="form-label">How long have you had these symptoms?</label>
          <ChipGroup
            options={DURATION_OPTIONS}
            value={draft.pain_duration || null}
            onChange={(v) => patch({ pain_duration: v })}
          />
        </div>
      )}
    </div>
  )
}

// ---------- Step 4: Your cycle ----------
function Step4Cycle({
  draft,
  patch,
}: {
  draft: DraftState
  patch: (updates: Partial<DraftState>) => void
}) {
  return (
    <div className="intake-step active">
      <h2 className="intake-title">Your cycle</h2>
      <p className="intake-desc">This helps us understand patterns in your pain.</p>

      <div className="form-field">
        <label className="form-label">Are you pregnant?</label>
        <ChipGroup
          options={PREGNANT_OPTIONS}
          value={draft.is_pregnant || null}
          onChange={(v) => patch({ is_pregnant: v })}
        />
      </div>

      <div className="form-field">
        <label className="form-label">How old were you when you got your first period?</label>
        <input
          className="form-input"
          type="text"
          inputMode="numeric"
          placeholder="e.g. 12"
          style={{ maxWidth: 120 }}
          value={draft.menarche_age == null ? '' : String(draft.menarche_age)}
          onChange={(e) => patch({ menarche_age: e.target.value })}
        />
      </div>

      <div className="form-field">
        <label className="form-label">Cycle length</label>
        <ChipGroup
          options={CYCLE_LENGTH_OPTIONS}
          value={draft.cycle_length || null}
          onChange={(v) => patch({ cycle_length: v })}
        />
      </div>

      <div className="form-field">
        <label className="form-label">Period duration</label>
        <ChipGroup
          options={PERIOD_DURATION_OPTIONS}
          value={draft.period_duration || null}
          onChange={(v) => patch({ period_duration: v })}
        />
      </div>

      <div className="form-field">
        <label className="form-label">Have you ever needed IV iron infusions for heavy bleeding or anemia?</label>
        <ChipGroup
          options={YES_NO_UNSURE_OPTIONS}
          value={draft.iv_iron || null}
          onChange={(v) => patch({ iv_iron: v })}
        />
      </div>

      <div className="form-field">
        <label className="form-label">Have you ever needed a blood transfusion?</label>
        <ChipGroup
          options={YES_NO_UNSURE_OPTIONS}
          value={draft.blood_transfusions || null}
          onChange={(v) => patch({ blood_transfusions: v })}
        />
      </div>
    </div>
  )
}

// ---------- Step 6: Mood & wellbeing (PHQ-9 + GAD-7) ----------
function Step6Wellbeing({
  draft,
  patch,
}: {
  draft: DraftState
  patch: (updates: Partial<DraftState>) => void
}) {
  const phq9 = draft.phq9_responses || Array(PHQ9_QUESTIONS.length).fill(null)
  const gad7 = draft.gad7_responses || Array(GAD7_QUESTIONS.length).fill(null)

  const setPhq9 = (i: number, v: number) => {
    const next = [...phq9]
    next[i] = next[i] === v ? null : v
    patch({ phq9_responses: next })
  }
  const setGad7 = (i: number, v: number) => {
    const next = [...gad7]
    next[i] = next[i] === v ? null : v
    patch({ gad7_responses: next })
  }

  return (
    <div className="intake-step active">
      <h2 className="intake-title">Mood &amp; wellbeing</h2>
      <p className="intake-desc">
        These are standard questions your care team uses to understand how you've been feeling.
        Answer what you can — there are no wrong answers, and you can skip any of them.
      </p>

      <div className="screener-block">
        <div className="screener-prompt">{PHQ9_PROMPT}</div>
        {PHQ9_QUESTIONS.map((q, i) => (
          <div className="screener-item" key={`phq-${i}`}>
            <div className="screener-q">{i + 1}. {q}</div>
            <ScreenerScale value={phq9[i] ?? null} onChange={(v) => setPhq9(i, v)} />
          </div>
        ))}
      </div>

      <div className="screener-block">
        <div className="screener-prompt">{GAD7_PROMPT}</div>
        {GAD7_QUESTIONS.map((q, i) => (
          <div className="screener-item" key={`gad-${i}`}>
            <div className="screener-q">{i + 1}. {q}</div>
            <ScreenerScale value={gad7[i] ?? null} onChange={(v) => setGad7(i, v)} />
          </div>
        ))}
      </div>
    </div>
  )
}

function ScreenerScale({
  value,
  onChange,
}: {
  value: number | null
  onChange: (v: number) => void
}) {
  return (
    <div className="chip-group chip-group-single screener-scale">
      {SCREENER_FREQUENCY_OPTIONS.map(([v, label]) => (
        <button
          key={v}
          type="button"
          className={`chip${value === v ? ' selected' : ''}`}
          onClick={() => onChange(v)}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

// ---------- Step 7: Recall your last 7 days ----------
function Step7Recall({
  days,
  meds,
  pain,
  onSetPain,
  adh,
  onToggleAdh,
  estimated,
  loaded,
  hasWearable,
  onAddMed,
}: {
  days: string[]
  meds: { id: number; name: string }[]
  pain: Record<string, number>
  onSetPain: (day: string, score: number | null) => void
  adh: Record<string, Record<number, boolean>>
  onToggleAdh: (day: string, medId: number) => void
  estimated: Set<string>
  loaded: boolean
  hasWearable: boolean
  onAddMed: (name: string) => void
}) {
  const [medDraft, setMedDraft] = useState('')
  const anyEstimated = estimated.size > 0

  const submitMed = () => {
    if (!medDraft.trim()) return
    onAddMed(medDraft)
    setMedDraft('')
  }

  return (
    <div className="intake-step active">
      <h2 className="intake-title">Fill in your last week</h2>
      <p className="intake-desc">
        {anyEstimated
          ? 'We estimated some days from your wearable — tap to adjust anything that feels off, then add your meds and check the days you took them.'
          : hasWearable
            ? "We couldn't estimate your last week yet, so tell us what you remember. Tap a pain score for each day, add your meds, and check the days you took them."
            : "A quick recap jump-starts your patterns. Tap a pain score for each day you remember, add your meds, and check the days you took them. Skip anything you don't recall."}
      </p>

      {/* Quick med entry — creates real medications so the adherence grid works */}
      <div className="recall-meds">
        <label className="form-label">What medications are you taking?</label>
        {meds.length > 0 && (
          <div className="recall-med-list">
            {meds.map((m) => (
              <span key={m.id} className="recall-med-tag">{m.name}</span>
            ))}
          </div>
        )}
        <div className="recall-med-add">
          <input
            className="form-input"
            placeholder="e.g. Norethindrone"
            value={medDraft}
            onChange={(e) => setMedDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submitMed()
              }
            }}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={submitMed}
            disabled={!medDraft.trim()}
          >
            Add
          </button>
        </div>
      </div>

      {/* Per-day recall grid */}
      {!loaded ? (
        <div className="intake-desc">Loading your week…</div>
      ) : (
        <div className="recall-days">
          {days.map((d) => {
            const score = pain[d]
            const dayMeds = adh[d] || {}
            return (
              <div key={d} className="recall-day">
                <div className="recall-day-head">
                  <span className="recall-day-label">{recallDayLabel(d)}</span>
                  {estimated.has(d) && <span className="recall-est-pill">estimated</span>}
                </div>
                <div className="recall-scale">
                  {Array.from({ length: 11 }, (_, i) => {
                    const active = score === i
                    return (
                      <button
                        key={i}
                        type="button"
                        className="recall-scale-btn"
                        style={
                          active
                            ? { background: painColor(i), color: painTextColor(i), borderColor: 'transparent' }
                            : undefined
                        }
                        onClick={() => onSetPain(d, active ? null : i)}
                      >
                        {i}
                      </button>
                    )
                  })}
                </div>
                {meds.length > 0 && (
                  <div className="recall-day-meds">
                    {meds.map((m) => {
                      const taken = !!dayMeds[m.id]
                      return (
                        <button
                          key={m.id}
                          type="button"
                          className={`recall-med-chip${taken ? ' taken' : ''}`}
                          style={taken ? { background: 'var(--accent)' } : undefined}
                          onClick={() => onToggleAdh(d, m.id)}
                        >
                          {taken ? '✓ ' : ''}
                          {m.name.split(' ')[0]}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------- Step 5: Recent periods (simplified LMP range picker) ----------
function Step5Periods({
  draft,
  patch,
}: {
  draft: DraftState
  patch: (updates: Partial<DraftState>) => void
}) {
  const ranges = draft.lmp_ranges || []
  const [monthOffset, setMonthOffset] = useState(0)
  const [pendingStart, setPendingStart] = useState<string | null>(null)

  const viewDate = useMemo(() => {
    const d = new Date()
    d.setDate(1)
    d.setMonth(d.getMonth() + monthOffset)
    return d
  }, [monthOffset])

  const monthLabel = viewDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })

  const daysInMonth = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth() + 1,
    0,
  ).getDate()
  const firstWeekday = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay()

  const today = new Date().toISOString().slice(0, 10)

  const isInRange = useCallback(
    (iso: string) => ranges.some((r) => r.start <= iso && iso <= r.end),
    [ranges],
  )

  const onDayClick = (iso: string) => {
    if (iso > today) return
    // If day already covered, remove that range entirely
    const covering = ranges.findIndex((r) => r.start <= iso && iso <= r.end)
    if (covering !== -1) {
      const next = ranges.filter((_, i) => i !== covering)
      patch({ lmp_ranges: next })
      return
    }
    if (!pendingStart) {
      setPendingStart(iso)
      return
    }
    const start = pendingStart < iso ? pendingStart : iso
    const end = pendingStart < iso ? iso : pendingStart
    // Merge into ranges (de-dupe overlapping)
    const merged = [...ranges.filter((r) => r.end < start || r.start > end), { start, end }]
    merged.sort((a, b) => b.start.localeCompare(a.start))
    patch({ lmp_ranges: merged })
    setPendingStart(null)
  }

  const cells: (string | null)[] = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    cells.push(iso)
  }

  return (
    <div className="intake-step active">
      <h2 className="intake-title">Recent periods</h2>
      <p className="intake-desc">
        Tap your period start, then your period end. Add as many recent cycles as you can — the
        more you share, the sharper your patterns become.
      </p>

      <div className="lmp-calendar">
        <div className="lmp-cal-header">
          <button
            type="button"
            className="lmp-cal-nav"
            onClick={() => setMonthOffset((v) => v - 1)}
            aria-label="Previous month"
          >
            ‹
          </button>
          <div className="lmp-cal-month">{monthLabel}</div>
          <button
            type="button"
            className="lmp-cal-nav"
            onClick={() => setMonthOffset((v) => Math.min(0, v + 1))}
            aria-label="Next month"
            disabled={monthOffset >= 0}
          >
            ›
          </button>
        </div>

        <div className="lmp-cal-weekdays">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i} className="lmp-cal-weekday">{d}</div>
          ))}
        </div>

        <div className="lmp-cal-grid">
          {cells.map((iso, i) => {
            if (!iso) return <div key={`e-${i}`} />
            const future = iso > today
            const selected = isInRange(iso)
            const pending = pendingStart === iso
            return (
              <button
                key={iso}
                type="button"
                className={`lmp-cal-day${selected ? ' selected' : ''}${pending ? ' pending' : ''}${future ? ' future' : ''}`}
                onClick={() => onDayClick(iso)}
                disabled={future}
              >
                {parseInt(iso.slice(8, 10), 10)}
              </button>
            )
          })}
        </div>
      </div>

      {ranges.length > 0 && (
        <div className="lmp-range-list">
          <div className="lmp-range-label">Logged periods:</div>
          {ranges.map((r) => (
            <div key={`${r.start}-${r.end}`} className="lmp-range-item">
              <span>
                {formatRangeLabel(r.start)} – {formatRangeLabel(r.end)}
              </span>
              <button
                type="button"
                aria-label="Remove period range"
                onClick={() => patch({ lmp_ranges: ranges.filter((x) => x !== r) })}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {pendingStart && (
        <div className="lmp-hint">
          Start: <strong>{formatRangeLabel(pendingStart)}</strong> — now tap the end day.
          <button
            type="button"
            className="lmp-hint-clear"
            onClick={() => setPendingStart(null)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

function formatRangeLabel(iso: string): string {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

// ---------- Chip groups (reusable) ----------
interface Option { value: string; label: string }

function ChipGroup({
  options,
  value,
  onChange,
}: {
  options: Option[]
  value: string | null
  onChange: (v: string | null) => void
}) {
  return (
    <div className="chip-group chip-group-single">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`chip${value === opt.value ? ' selected' : ''}`}
          onClick={() => onChange(value === opt.value ? null : opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function MultiChipGroup({
  options,
  values,
  onToggle,
}: {
  options: Option[]
  values: string[]
  onToggle: (v: string) => void
}) {
  return (
    <div className="chip-group">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`chip${values.includes(opt.value) ? ' selected' : ''}`}
          onClick={() => onToggle(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// ---------- Step 0: Connect a wearable ----------
// Live OAuth providers the user can connect right here. Both go through the same
// generic connect flow (`/api/wearables/<provider>/authorize` → OAuth redirect).
const SUPPORTED_WEARABLES: { provider: string; name: string; sub: string; logo: ReactNode }[] = [
  {
    provider: 'oura',
    name: 'Oura Ring',
    sub: 'Sleep, HRV, readiness, body temperature.',
    logo: (
      <svg viewBox="0 0 32 32" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="16" cy="16" r="11" />
        <circle cx="16" cy="16" r="6" />
      </svg>
    ),
  },
  {
    provider: 'whoop',
    name: 'WHOOP',
    sub: 'Strain, recovery, sleep, resting HR.',
    logo: (
      <svg viewBox="0 0 32 32" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="5" width="14" height="22" rx="5" />
        <path d="M12.5 13h7M12.5 19h7" />
      </svg>
    ),
  },
]

function Step0Wearables({
  connected,
  pendingProvider,
  onConnect,
}: {
  connected: Record<string, boolean>
  pendingProvider: string | null
  onConnect: (provider: string) => void
}) {
  const anyPending = pendingProvider != null
  return (
    <div className="intake-step active">
      <h2 className="intake-title" style={{ textAlign: 'center' }}>
        Connect your wearable
      </h2>
      <p
        className="intake-desc"
        style={{ textAlign: 'center', maxWidth: 380, margin: '0 auto 1rem' }}
      >
        Optional — but powerful. When you connect a device, we'll auto-fill your daily check-ins and
        estimate pain scores for days you forgot to log, using your sleep, HRV, and recovery
        signals.
      </p>

      <div className="wearable-choice-list">
        {SUPPORTED_WEARABLES.map((w) =>
          connected[w.provider] ? (
            <div key={w.provider} className="wearable-choice connected">
              <div className="wearable-choice-logo" aria-hidden>{w.logo}</div>
              <div className="wearable-choice-body">
                <div className="wearable-choice-name">{w.name}</div>
                <div className="wearable-choice-sub">
                  Connected — we'll pull your biometrics automatically.
                </div>
              </div>
              <span className="wearable-choice-check" aria-label="Connected">✓</span>
            </div>
          ) : (
            <button
              key={w.provider}
              type="button"
              className="wearable-choice wearable-choice-connectable"
              onClick={() => onConnect(w.provider)}
              disabled={anyPending}
            >
              <div className="wearable-choice-logo" aria-hidden>{w.logo}</div>
              <div className="wearable-choice-body">
                <div className="wearable-choice-name">{w.name}</div>
                <div className="wearable-choice-sub">{w.sub}</div>
              </div>
              <span className="wearable-choice-connect">
                {pendingProvider === w.provider ? 'Redirecting…' : 'Connect'}
              </span>
            </button>
          ),
        )}

        {[
          { name: 'Apple Watch', sub: 'Heart rate, sleep, activity.' },
          { name: 'Google Health', sub: 'Sleep, heart rate, activity from Android.' },
        ].map((p) => (
          <div key={p.name} className="wearable-choice coming-soon" aria-disabled="true">
            <div className="wearable-choice-logo" aria-hidden>
              <svg viewBox="0 0 32 32" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <rect x="8" y="8" width="16" height="16" rx="4" />
              </svg>
            </div>
            <div className="wearable-choice-body">
              <div className="wearable-choice-name">{p.name}</div>
              <div className="wearable-choice-sub">{p.sub}</div>
            </div>
            <span className="wearable-choice-soon">Coming soon</span>
          </div>
        ))}
      </div>
    </div>
  )
}
